import { useCallback, useReducer, useState } from 'react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from '@xyflow/react';

import { NodeType } from './use-sidebar-control/use-sidebar-control.types.js';
import { ADD_NODE_PLACEHOLDER_ID, INITIAL_ENROLL_NODE_ID } from '../constants/constants.js';
import {
  appendNodeAtEnd,
  healEdgesAfterNodeRemoval,
  recalculateLinearPosition,
  shouldResetToStart,
} from '../workflow-graph/workflow-graph-actions';
import { mapCanvasContent } from '../mappers/map-canvas-content.js';

type GraphState = { nodes: Node[]; edges: Edge[] };

const initialNode: Node = {
  id: INITIAL_ENROLL_NODE_ID,
  type: NodeType.Enroll,
  position: { x: 200, y: 200 },
  data: {},
};

function getInitialGraphState(): GraphState {
  return {
    nodes: [initialNode],
    edges: [],
  };
}

function stripLegacyPlaceholder(state: GraphState): GraphState {
  return {
    nodes: state.nodes.filter((n) => n.id !== ADD_NODE_PLACEHOLDER_ID),
    edges: state.edges.filter(
      (e) => e.target !== ADD_NODE_PLACEHOLDER_ID && e.source !== ADD_NODE_PLACEHOLDER_ID
    ),
  };
}

type GraphAction =
  | { type: 'ADD_NODE_AT_PLACEHOLDER'; nodeType: NodeType; data?: Record<string, unknown> }
  | { type: 'NODES_CHANGE'; changes: NodeChange[] }
  | { type: 'EDGES_CHANGE'; changes: EdgeChange[] }
  | { type: 'UPDATE_NODE_DATA'; nodeId: string; data: Record<string, unknown> }
  | { type: 'RESET_TO_START' };

function graphReducer(state: GraphState, action: GraphAction): GraphState {
  switch (action.type) {
    case 'ADD_NODE_AT_PLACEHOLDER': {
      const stripped = stripLegacyPlaceholder(state);
      const result = appendNodeAtEnd({
        nodes: stripped.nodes,
        edges: stripped.edges,
        nodeType: action.nodeType,
        data: action.data,
      });
      return {
        nodes: recalculateLinearPosition(result.nodes),
        edges: result.edges,
      };
    }
    case 'NODES_CHANGE': {
      const filteredChanges = action.changes.filter((change) => {
        if (change.type === 'remove' && change.id === ADD_NODE_PLACEHOLDER_ID) {
          return false;
        }
        return true;
      });
      const removedNodes = filteredChanges.filter((c) => c.type === 'remove');
      const removedNodeIds = removedNodes.map((r) => (r as { type: 'remove'; id: string }).id);

      if (removedNodeIds.length > 0) {
        const nextNodes = applyNodeChanges(filteredChanges, state.nodes);
        const hasFirstEnroll = nextNodes.some(
          (n) => n.id === INITIAL_ENROLL_NODE_ID && n.type === NodeType.Enroll
        );
        if (shouldResetToStart(nextNodes) || !hasFirstEnroll) {
          return getInitialGraphState();
        }
        const nextEdges = healEdgesAfterNodeRemoval({
          edges: state.edges,
          removedNodeIds,
        });
        const cleaned = stripLegacyPlaceholder({ nodes: nextNodes, edges: nextEdges });
        return {
          nodes: recalculateLinearPosition(cleaned.nodes),
          edges: cleaned.edges,
        };
      }
      return stripLegacyPlaceholder({
        nodes: applyNodeChanges(filteredChanges, state.nodes),
        edges: state.edges,
      });
    }
    case 'EDGES_CHANGE': {
      const filteredChanges = action.changes.filter((change) => change.type !== 'remove');
      return {
        nodes: state.nodes,
        edges: applyEdgeChanges(filteredChanges, state.edges),
      };
    }
    case 'UPDATE_NODE_DATA': {
      const nextNodes = state.nodes.map((node) =>
        node.id === action.nodeId ? { ...node, data: { ...node.data, ...action.data } } : node
      );
      return stripLegacyPlaceholder({ nodes: nextNodes, edges: state.edges });
    }
    case 'RESET_TO_START':
      return getInitialGraphState();
    default:
      return state;
  }
}

type FirstEnrollConfiguredData = {
  orBlocks?: {
    andBlocks?: { entityType?: string; entityId?: unknown }[];
  }[];
};

function isFirstEnrollConfigured(node: Node): boolean {
  const data = node.data as FirstEnrollConfiguredData;
  if (!Array.isArray(data?.orBlocks)) {
    return false;
  }
  return data.orBlocks.some((orBlock) => {
    if (!Array.isArray(orBlock?.andBlocks)) {
      return false;
    }
    return orBlock.andBlocks.some((andBlock) => {
      if (!andBlock?.entityType) {
        return false;
      }
      const ids = andBlock.entityId;
      return Array.isArray(ids) && ids.length > 0;
    });
  });
}

function getLeftmostEnrollNode(nodes: Node[]): Node | undefined {
  const enrolls = nodes.filter((n) => n.type === NodeType.Enroll);
  if (enrolls.length === 0) {
    return undefined;
  }
  return [...enrolls].sort((a, b) => a.position.x - b.position.x)[0];
}

export type WorkflowGraphControl = ReturnType<typeof useWorkflowGraph>;

export type WorkflowGraphInitialState = GraphState;

export const useWorkflowGraph = (initialState?: WorkflowGraphInitialState) => {
  const [isNodeSidebarOpen, setIsNodeSidebarOpen] = useState(false);
  const [graphState, dispatch] = useReducer(
    graphReducer,
    initialState ?? getInitialGraphState(),
    (base) => stripLegacyPlaceholder(base)
  );
  const { nodes, edges } = graphState;

  const openSidebar = useCallback(() => setIsNodeSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsNodeSidebarOpen(false), []);

  const addNodeAtPlaceholder = useCallback((nodeType: NodeType, data?: Record<string, unknown>) => {
    dispatch({ type: 'ADD_NODE_AT_PLACEHOLDER', nodeType, data });
    setIsNodeSidebarOpen(false);
  }, []);

  const addEnroll = useCallback(
    (data: Record<string, unknown>) => {
      addNodeAtPlaceholder(NodeType.Enroll, data);
    },
    [addNodeAtPlaceholder]
  );

  const onNodesChange = useCallback((changedNodes: NodeChange[]) => {
    dispatch({ type: 'NODES_CHANGE', changes: changedNodes });
  }, []);

  const onEdgesChange = useCallback((edgeChanges: EdgeChange[]) => {
    const filteredChanges = edgeChanges.filter((change) => change.type !== 'remove');
    dispatch({ type: 'EDGES_CHANGE', changes: filteredChanges });
  }, []);

  const mapNodesToDto = useCallback(() => {
    return mapCanvasContent({ nodes });
  }, [nodes]);

  const updateNodeData = useCallback((nodeId: string, data: Record<string, unknown>) => {
    dispatch({ type: 'UPDATE_NODE_DATA', nodeId, data });
  }, []);

  const isGraphValidForCreate = useCallback(() => {
    const firstEnrollNode = getLeftmostEnrollNode(nodes);
    if (!firstEnrollNode) {
      return false;
    }
    return isFirstEnrollConfigured(firstEnrollNode);
  }, [nodes]);

  return {
    nodes,
    edges,
    isNodeSidebarOpen,
    addEnroll,
    openSidebar,
    closeSidebar,
    addNodeAtPlaceholder,
    isGraphValidForCreate,
    onNodesChange,
    onEdgesChange,
    mapNodesToDto,
    updateNodeData,
  };
};
